import React, { useEffect, useMemo } from 'react';
import { Crown, Search, Users } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useServerStore } from '../../store/serverStore';
import AppBadge from '../ui/AppBadge';
import UserProfileLink from '../profile/UserProfileLink';

interface ServerMembersPanelProps {
  serverId: string;
}

const getDisplayName = (member: any) =>
  member.user?.display_name || member.profile?.display_name || member.user?.username || member.profile?.username || 'Unknown user';

const getUsername = (member: any) =>
  member.user?.username || member.profile?.username || 'unknown';

const getStatus = (member: any) =>
  member.user?.status || member.profile?.status || 'offline';

const getAvatar = (member: any) =>
  member.user?.avatar_url || member.profile?.avatar_url || null;

const getHighestColoredRole = (member: any) => {
  return [...(member.roles || [])]
    .filter((role: any) => !role.is_everyone && role.color)
    .sort((a: any, b: any) => (b.position || 0) - (a.position || 0))[0];
};

const getHighestHoistedRole = (member: any) => {
  return [...(member.roles || [])]
    .filter((role: any) => !role.is_everyone && role.hoist)
    .sort((a: any, b: any) => (b.position || 0) - (a.position || 0))[0];
};

const ServerMembersPanel: React.FC<ServerMembersPanelProps> = ({ serverId }) => {
  const { members, fetchServerMembers } = useServerStore();
  const { profile } = useAuthStore();
  const server = useServerStore((state) => state.currentServer);

  useEffect(() => {
    fetchServerMembers(serverId, { silent: true });
  }, [serverId, fetchServerMembers]);

  const grouped = useMemo(() => {
    const sorted = [...members].sort((a: any, b: any) => {
      const statusRank = (status: string) => (status === 'offline' ? 1 : 0);
      const statusDelta = statusRank(getStatus(a)) - statusRank(getStatus(b));
      if (statusDelta !== 0) return statusDelta;
      return getDisplayName(a).localeCompare(getDisplayName(b));
    });

    const hoistedGroups = new Map<string, { role: any; members: any[] }>();
    const regular: any[] = [];
    for (const member of sorted) {
      const hoistedRole = getHighestHoistedRole(member);
      if (!hoistedRole) {
        regular.push(member);
        continue;
      }
      const existing = hoistedGroups.get(hoistedRole.id) || { role: hoistedRole, members: [] };
      existing.members.push(member);
      hoistedGroups.set(hoistedRole.id, existing);
    }

    return {
      hoisted: [...hoistedGroups.values()].sort((a, b) => (b.role.position || 0) - (a.role.position || 0)),
      online: regular.filter((member: any) => getStatus(member) !== 'offline'),
      offline: regular.filter((member: any) => getStatus(member) === 'offline'),
    };
  }, [members]);

  const renderMember = (member: any) => {
    const isBot = Boolean(member.bot || member.user?.bot || member.user?.is_system || member.profile?.bot);
    const isOwner = server?.owner_id === member.user_id;
    const isSelf = profile?.id === member.user_id;
    const displayName = getDisplayName(member);
    const username = getUsername(member);
    const avatar = getAvatar(member);
    const status = getStatus(member);
    const coloredRole = getHighestColoredRole(member);

    return (
      <UserProfileLink key={member.user_id || member.id} userId={member.user_id}>
        <div className="group flex items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-highlight/40 transition-colors cursor-pointer">
          <div className="relative flex-shrink-0">
            <div className="w-8 h-8 rounded-full overflow-hidden bg-highlight">
              {avatar ? (
                <img src={avatar} alt={displayName} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-accent-color/80 text-white flex items-center justify-center text-xs font-bold">
                  {displayName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div
              className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-bg-secondary ${
                status === 'online' ? 'bg-success' : 'bg-text-muted/40'
              }`}
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 min-w-0">
              <span
                className="truncate text-sm font-medium"
                style={{ color: coloredRole?.color || undefined }}
              >
                {displayName}
              </span>
              {isOwner && <Crown className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" />}
              {isBot && <AppBadge className="px-1.5 py-0 text-[9px]" />}
              {isSelf && <span className="text-[10px] text-text-muted flex-shrink-0">you</span>}
            </div>
            <div className="text-[11px] text-text-muted truncate leading-tight">@{username}</div>
          </div>
        </div>
      </UserProfileLink>
    );
  };

  const renderGroup = (label: string, list: any[]) => {
    if (list.length === 0) return null;
    return (
      <section className="mb-4">
        <h3 className="px-2 mb-1.5 text-[11px] uppercase tracking-wide font-bold text-text-muted">
          {label} — {list.length}
        </h3>
        <div className="space-y-0.5">{list.map(renderMember)}</div>
      </section>
    );
  };

  return (
    <aside className="w-64 flex-shrink-0 bg-bg-secondary/85 border-l border-border-color flex flex-col overflow-hidden">
      <div className="h-12 px-4 border-t border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-bold text-text-primary">
          <Users className="w-4 h-4 text-text-muted" />
          Members
        </div>
        <span className="text-xs text-text-muted">{members.length}</span>
      </div>

      <div className="px-3 py-2">
        <div className="h-8 rounded-md bg-bg-primary/60 border border-border-color flex items-center gap-2 px-2 text-text-muted">
          <Search className="w-3.5 h-3.5" />
          <span className="text-xs">Browse members</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-4">
        {members.length > 0 ? (
          <>
            {grouped.hoisted.map((group) => renderGroup(group.role.name, group.members))}
            {renderGroup('Online', grouped.online)}
            {renderGroup('Offline', grouped.offline)}
          </>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-center px-6">
            <Users className="w-10 h-10 text-text-muted mb-3" />
            <p className="text-sm text-text-secondary">No members visible</p>
          </div>
        )}
      </div>
    </aside>
  );
};

export default ServerMembersPanel;
